import { SphereSupportLink } from "@components/common/sphere-support-link";
import { ContactSupportLocations } from "@custom-types/types";
import { Banner, Text } from "@flat-ui";
import { Box } from "@mui/material";
import { REQUEST_A_QUOTE_URL } from "@src/constants/subscription-constants";
import { DateTimeUtils } from "@stellar/utils";
import { topPriorityXgSubscriptionSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { nounPluralize } from "@utils/data-display";
import { OPEN_OTHERS_TARGET_ATTRIBUTE } from "@utils/project-utils";
import { isTrialSubscription } from "@utils/subscription-utils";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useMemo, useState } from "react";

/** Default remaining days to start showing the banner */
const DEFAULT_DAYS = 3;

interface Props {
  /** Remaining days to start showing the banner */
  days?: number;
}

/** Banner to show when a trial subscription is about to expire */
export function TrialExpirationBanner({
  days = DEFAULT_DAYS,
}: Props): JSX.Element | null {
  const subscription = useAppSelector(topPriorityXgSubscriptionSelector);
  const { trackEvent } = useTrackEvent();
  const isTrial = isTrialSubscription(subscription);
  const [isDismissed, setIsDismissed] = useState<boolean>(false);

  const remainingDays = useMemo(() => {
    const expirationDate = isTrial ? subscription?.endDate : undefined;
    if (!expirationDate) {
      return days + 1;
    }
    return DateTimeUtils.diffFromTodayInDays({
      date: expirationDate,
      shouldIncludeToday: false,
    });
  }, [days, subscription, isTrial]);

  const description = useMemo(() => {
    const name = subscription ? subscription.label : "trial";
    return remainingDays > 0
      ? `Your ${name} subscription ends in ${nounPluralize({
          counter: remainingDays,
          word: "day",
        })}.`
      : `Your ${name} subscription has ended.`;
  }, [remainingDays, subscription]);

  const shouldShowBanner = useMemo(() => {
    return remainingDays <= days && isTrial && !isDismissed;
  }, [days, isDismissed, remainingDays, isTrial]);

  const onRequestAQuote = useCallback((): void => {
    trackEvent({
      name: WorkspaceEvents.requestAQuote,
    });
    setIsDismissed(true);
    window.open(REQUEST_A_QUOTE_URL, OPEN_OTHERS_TARGET_ATTRIBUTE);
  }, [trackEvent]);

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <Box data-testid="trial-expiration-banner" component="div">
      <Banner
        variant="warning"
        action={{
          label: "Request a quote",
          action: onRequestAQuote,
        }}
        onClose={() => setIsDismissed(true)}
      >
        <Text variant="bodyM">
          {description} Request a quote or contact{" "}
          <SphereSupportLink
            location={ContactSupportLocations.subscriptionExpiredBanner}
          />{" "}
          for further information.
        </Text>
      </Banner>
    </Box>
  );
}
